/**
 * Needs You — the human-task inbox, the capability gate's other half.
 * Everything the Oracle can't or must not do itself (engaging on other
 * accounts, running ads, Instagram settings, re-authentication, filming)
 * lands here as a task with expert instructions.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Badge, EmptyState, Spinner, useToast } from '../components/ui';
import { TasksIcon } from '../components/NavIcons';
import { getHumanTasks, updateHumanTaskStatus } from '../api';
import type { HumanTask, HumanTaskStatus, PageKey } from '../types';
import './TasksPage.css';

interface Props {
  workspaceId: number;
  onNavigate: (_page: PageKey) => void;
  onTasksChanged?: () => void;
}

const CATEGORY_LABEL: Record<string, string> = {
  engagement: 'Engagement',
  ads: 'Ads',
  account_setup: 'Account setup',
  verification: 'Verification',
  content: 'Content',
  other: 'Task',
};

const SOURCE_LABEL: Record<string, string> = {
  oracle: 'From the Oracle',
  system: 'From Conistra',
  user: 'Added by you',
};

type StatusFilter = HumanTaskStatus;

const FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'done', label: 'Done' },
  { id: 'dismissed', label: 'Dismissed' },
];

function relTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function dueLabel(iso: string | null): { text: string; overdue: boolean } | null {
  if (!iso) return null;
  const due = new Date(iso);
  const diff = due.getTime() - Date.now();
  if (diff < 0) return { text: `Due ${relTime(iso)}`, overdue: true };
  const h = Math.ceil(diff / 3600000);
  if (h <= 24) return { text: `Due in ${h}h`, overdue: false };
  return { text: `Due ${due.toLocaleDateString()}`, overdue: false };
}

export default function TasksPage({ workspaceId, onNavigate, onTasksChanged }: Props) {
  const { toast } = useToast();
  const [filter, setFilter] = useState<StatusFilter>('open');
  const [tasks, setTasks] = useState<HumanTask[]>([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHumanTasks(workspaceId, { status: filter, limit: 100 });
      setTasks(res.tasks);
      setOpenCount(res.open_count);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, filter]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const setStatus = async (task: HumanTask, status: HumanTaskStatus) => {
    setBusyId(task.id);
    try {
      await updateHumanTaskStatus(workspaceId, task.id, status);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      setOpenCount((prev) =>
        status === 'open' ? prev + 1 : task.status === 'open' ? Math.max(0, prev - 1) : prev
      );
      onTasksChanged?.();
      if (status === 'done') toast('Nice — marked as done.', 'success');
      else if (status === 'dismissed') toast('Task dismissed.', 'success');
      else toast('Task reopened.', 'success');
    } catch (err: any) {
      toast(err?.message ?? 'Could not update the task.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="tasks-page">
      <div className="tasks-hero">
        <div className="tasks-hero__icon">
          <TasksIcon size={26} />
        </div>
        <div>
          <div className="tasks-hero__title">Needs You</div>
          <div className="tasks-hero__subtitle">
            Some moves need a human touch — engaging with other accounts, running ads, or
            fixing your connection. When your strategy calls for one, it lands here with
            step-by-step instructions. Tell the Oracle when you're done, or check it off here.
          </div>
        </div>
      </div>

      <div className="tasks-filters" role="tablist" aria-label="Filter tasks by status">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            role="tab"
            aria-selected={filter === f.id}
            className={`tasks-chip${filter === f.id ? ' tasks-chip--active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            {f.id === 'open' && openCount > 0 && (
              <span className="tasks-chip__count">{openCount}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <Spinner size="md" />
      ) : tasks.length === 0 ? (
        filter === 'open' ? (
          <EmptyState
            icon={<TasksIcon size={28} />}
            title="You're all caught up"
            description="Nothing needs your attention right now. When the Oracle's strategy calls for something only you can do, it will show up here."
            action={
              <Button variant="secondary" size="sm" onClick={() => onNavigate('assistant')}>
                Ask the Oracle
              </Button>
            }
          />
        ) : (
          <EmptyState
            title={filter === 'done' ? 'No completed tasks yet' : 'No dismissed tasks'}
            description={
              filter === 'done'
                ? 'Tasks you check off will show up here.'
                : 'Tasks you dismiss will show up here.'
            }
          />
        )
      ) : (
        <div className="tasks-list">
          {tasks.map((t) => (
            <TaskCard
              key={t.id}
              task={t}
              busy={busyId === t.id}
              onSetStatus={(status) => setStatus(t, status)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TaskCard({
  task: t,
  busy,
  onSetStatus,
}: {
  task: HumanTask;
  busy: boolean;
  onSetStatus: (_status: HumanTaskStatus) => void;
}) {
  const due = dueLabel(t.due_at);
  const isOpen = t.status === 'open';

  return (
    <div className={`task-card${isOpen ? '' : ' task-card--resolved'}`}>
      <div className="task-card__header">
        <div className="task-card__badges">
          {t.priority === 'high' && (
            <Badge variant="error" dot>
              High priority
            </Badge>
          )}
          <Badge variant="neutral">{CATEGORY_LABEL[t.category] ?? t.category}</Badge>
          {due && (
            <Badge variant={due.overdue ? 'error' : 'scheduled'}>{due.text}</Badge>
          )}
        </div>
        <span className="task-card__time">{relTime(t.created_at)}</span>
      </div>

      <div className="task-card__title">{t.title}</div>
      {t.instructions && <div className="task-card__instructions">{t.instructions}</div>}

      <div className="task-card__footer">
        <span className="task-card__source">{SOURCE_LABEL[t.source] ?? ''}</span>
        <div className="task-card__actions">
          {isOpen ? (
            <>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => onSetStatus('dismissed')}>
                Dismiss
              </Button>
              <Button size="sm" variant="primary" loading={busy} onClick={() => onSetStatus('done')}>
                Mark done
              </Button>
            </>
          ) : (
            <Button size="sm" variant="secondary" loading={busy} onClick={() => onSetStatus('open')}>
              Reopen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
